import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useMemo,
} from 'react';

import { usePersistedJsonState } from '@superfunnel/react-hooks/usePersistedState';
import { AgentAutonomyMode } from '@superfunnel/types/agent-definition';
import { parseCustomModelOptionId } from '@superfunnel/types/super-agent-custom-model';
import { SuperAgentModelSelection } from '@superfunnel/types/super-agent-model-catalog';

import { safeLocalStorage } from '../utils/local-storage';
import { UserContext } from './UserContext';

const DEFAULT_MODEL_SELECTION: SuperAgentModelSelection = { optionId: 'auto' };

interface PersistedModelSelection extends SuperAgentModelSelection {
  accountId?: string;
}

interface SuperAgentPreferences {
  modelSelection: SuperAgentModelSelection;
  setModelSelection: (selection: SuperAgentModelSelection) => void;
  autonomyMode: AgentAutonomyMode;
  setAutonomyMode: Dispatch<SetStateAction<AgentAutonomyMode>>;
}

const SuperAgentPreferencesContext =
  createContext<SuperAgentPreferences | null>(null);

/**
 * Holds the model, thinking and approval choices once for the whole app, so
 * the SuperAgent page, the Ask AI panel and the editor agents share them.
 */
export function SuperAgentPreferencesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { selectedAccount } = useContext(UserContext);
  const [persistedModelSelection, setPersistedModelSelection] =
    usePersistedJsonState<PersistedModelSelection>(
      'superagent.modelSelection',
      DEFAULT_MODEL_SELECTION,
      safeLocalStorage,
    );
  const [autonomyMode, setAutonomyMode] =
    usePersistedJsonState<AgentAutonomyMode>(
      'superagent.autonomyMode',
      'ask',
      safeLocalStorage,
    );

  const value = useMemo<SuperAgentPreferences>(() => {
    const { accountId: selectionAccountId, ...selection } =
      persistedModelSelection;
    const isOtherAccountCustomModel =
      parseCustomModelOptionId(selection.optionId) &&
      selectionAccountId !== selectedAccount?.id;

    return {
      modelSelection: isOtherAccountCustomModel
        ? DEFAULT_MODEL_SELECTION
        : selection,
      setModelSelection: (nextSelection) =>
        setPersistedModelSelection({
          ...nextSelection,
          accountId: selectedAccount?.id,
        }),
      autonomyMode,
      setAutonomyMode,
    };
  }, [
    autonomyMode,
    persistedModelSelection,
    selectedAccount?.id,
    setAutonomyMode,
    setPersistedModelSelection,
  ]);

  return (
    <SuperAgentPreferencesContext.Provider value={value}>
      {children}
    </SuperAgentPreferencesContext.Provider>
  );
}

export function useSuperAgentPreferences() {
  const preferences = useContext(SuperAgentPreferencesContext);
  if (!preferences) {
    throw new Error(
      'useSuperAgentPreferences must be used within SuperAgentPreferencesProvider',
    );
  }
  return preferences;
}
